import { useColorScheme } from '@/hooks/useColorScheme';
import { cn } from '@/utils/tailwind';

import { RunSchema } from '../types/schema';

export const useTextColor = (
  willOverflow: boolean,
  isActive: boolean,
  isFiltered: boolean
) => {
  const { isDarkMode } = useColorScheme();
  if (isFiltered) return 'black';
  if (willOverflow) return isDarkMode ? 'white' : 'black';
  if (isActive) return 'white';

  return isDarkMode ? 'white' : 'black';
};

export const useRunTypeColors = (
  runType: RunSchema['run_type'],
  runName: string
) => {
  const { isDarkMode } = useColorScheme();
  const lightColors = {
    chain: {
      bg: 'rgba(188, 229, 251, 1)',
      hover: 'rgba(240, 249, 255, 1)',
      hoverBorder: 'rgba(61, 188, 243, 1)',
      active: 'rgba(61, 188, 243, 1)',
      icon: 'rgba(15, 73, 107, 1)',
    },
    sequence: {
      bg: 'rgba(178, 204, 255, 1)',
      hover: 'rgba(239, 244, 255, 1)',
      hoverBorder: 'rgba(132, 173, 255, 1)',
      active: 'rgba(82, 139, 255, 1)',
      icon: 'rgba(10, 89, 130, 1)',
    },
    llm: {
      bg: 'rgba(245, 204, 157, 1)',
      hover: 'rgba(255, 235, 213, 1)',
      hoverBorder: 'rgba(255, 136, 0, 1)',
      active: 'rgba(255, 136, 0, 1)',
      icon: 'rgba(203, 108, 0, 1)',
    },
    retriever: {
      bg: 'rgba(241, 189, 220, 1)',
      hover: 'rgba(251, 220, 239, 1)',
      hoverBorder: 'rgba(206, 121, 172, 1)',
      active: 'rgba(230, 128, 189, 1)',
      icon: 'rgba(182, 65, 135, 1)',
    },
    parser: {
      bg: 'rgba(171, 216, 222, 1)',
      hover: 'rgba(205, 230, 233, 1)',
      hoverBorder: 'rgba(112, 152, 157, 1)',
      active: 'rgba(112, 152, 157, 1)',
      icon: 'rgba(96, 135, 140, 1)',
    },
    tool: {
      bg: 'rgba(147, 225, 201, 1)',
      hover: 'rgba(223, 247, 240, 1)',
      hoverBorder: 'rgba(8, 167, 119, 1)',
      active: 'rgba(17, 177, 129, 1)',
      icon: 'rgba(11, 114, 83, 1)',
    },
    prompt: {
      bg: 'rgba(218, 214, 254, 1)',
      hover: 'rgba(244, 243, 255, 1)',
      hoverBorder: 'rgba(159, 139, 250, 1)',
      active: 'rgba(127, 92, 246, 1)',
      icon: 'rgba(96, 40, 217, 1)',
    },
  };

  const darkColors = {
    chain: {
      bg: 'rgba(15, 73, 107, 1)',
      hover: 'rgba(10, 47, 71, 1)',
      hoverBorder: 'rgba(7, 102, 153, 1)',
      active: 'rgba(20, 164, 227, 1)',
      icon: 'rgba(7, 131, 194, 1)',
    },
    sequence: {
      bg: 'rgba(0, 53, 158, 1)',
      hover: 'rgba(0, 34, 102, 1)',
      hoverBorder: 'rgba(21, 94, 239, 1)',
      active: 'rgba(41, 112, 255, 1)',
      icon: 'rgba(132, 173, 255, 1)',
    },
    llm: {
      bg: 'rgba(112, 65, 11, 1)',
      hover: 'rgba(46, 24, 0, 1)',
      hoverBorder: 'rgba(164, 87, 0, 1)',
      active: 'rgba(255, 136, 0, 1)',
      icon: 'rgba(254, 141, 13, 1)',
    },
    retriever: {
      bg: 'rgba(85, 40, 66, 1)',
      hover: 'rgba(46, 11, 31, 1)',
      hoverBorder: 'rgba(186, 55, 124, 1)',
      active: 'rgba(202, 68, 138, 1)',
      icon: 'rgba(219, 139, 185, 1)',
    },
    parser: {
      bg: 'rgba(42, 64, 67, 1)',
      hover: 'rgba(23, 45, 48, 1)',
      hoverBorder: 'rgba(12, 105, 117, 1)',
      active: 'rgba(57, 131, 141, 1)',
      icon: 'rgba(121, 172, 179, 1)',
    },
    tool: {
      bg: 'rgba(0, 81, 57, 1)',
      hover: 'rgba(0, 55, 39, 1)',
      hoverBorder: 'rgba(10, 149, 107, 1)',
      active: 'rgba(0, 165, 115, 1)',
      icon: 'rgba(0, 168, 117, 1)',
    },
    prompt: {
      bg: 'rgba(96, 76, 148, 1)',
      hover: 'rgba(39, 30, 62, 1)',
      hoverBorder: 'rgba(96, 40, 217, 1)',
      active: 'rgba(127, 92, 246, 1)',
      icon: 'rgba(159, 139, 250, 1)',
    },
  };

  const colors = isDarkMode ? darkColors : lightColors;

  return (runName === 'RunnableParallel' || runName === 'RunnableSequence') &&
    runType === 'chain'
    ? colors.sequence
    : colors[runType] ?? colors.chain;
};

export const getRunTypeClassName = (
  run: Pick<RunSchema, 'run_type' | 'name'> | undefined | null
) => {
  if (run == null) return undefined;

  if (run.run_type === 'tool') {
    return cn('bg-[rgb(17,177,129)] text-white dark:bg-[rgb(0,165,115)]');
  }

  if (run.run_type === 'retriever') {
    return cn('bg-[rgb(230,128,189)] text-white dark:bg-[rgb(202,68,138)]');
  }

  if (run.run_type === 'parser') {
    return cn('bg-[rgb(112,142,157)] text-white dark:bg-[rgb(57,131,141)]');
  }

  if (run.run_type === 'prompt') {
    return cn('bg-[rgb(127,92,246)] text-white dark:bg-[rgb(127,92,246)]');
  }

  if (run.run_type === 'llm') {
    return cn('bg-[rgb(255,136,0)] text-white dark:bg-[rgb(255,136,0)]');
  }

  if (run.run_type === 'chain') {
    if (run.name === 'RunnableParallel' || run.name === 'RunnableSequence') {
      return cn('bg-[rgb(41,112,255)] text-white dark:bg-[rgb(41,112,255)]');
    }
    return cn('bg-[rgb(61,188,243)] text-white dark:bg-[rgb(20,164,227)]');
  }

  // Default case (using chain colors)
  return cn('bg-[rgb(61,188,243)] text-white dark:bg-[rgb(20,164,227)]');
};
